import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, ExternalLink, Eye, EyeOff } from "lucide-react";

const EMPTY = {
  title: "", slug: "", excerpt: "", content: "",
  cover_image_url: "", tags: "", status: "draft", meta_description: "",
};

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // null | post object | "new"
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    try {
      const { data } = await api.get("/admin/blog");
      setPosts(data);
    } catch {
      toast.error("Failed to load posts");
    }
  };

  useEffect(() => { reload(); }, []);

  useEffect(() => {
    if (editing === "new") setForm(EMPTY);
    else if (editing) setForm({
      title: editing.title || "",
      slug: editing.slug || "",
      excerpt: editing.excerpt || "",
      content: editing.content || "",
      cover_image_url: editing.cover_image_url || "",
      tags: (editing.tags || []).join(", "),
      status: editing.status || "draft",
      meta_description: editing.meta_description || "",
    });
  }, [editing]);

  const set = (k) => (e) => setForm({ ...form, [k]: typeof e === "string" ? e : e.target.value });

  const save = async () => {
    if (!form.title.trim() || !form.excerpt.trim() || !form.content.trim()) {
      toast.error("Title, excerpt, and content are required");
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      slug: form.slug.trim() || undefined,
      excerpt: form.excerpt.trim(),
      content: form.content,
      cover_image_url: form.cover_image_url.trim() || null,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      status: form.status,
      meta_description: form.meta_description.trim() || null,
    };
    try {
      if (editing === "new") {
        await api.post("/admin/blog", payload);
        toast.success("Post created");
      } else {
        await api.patch(`/admin/blog/${editing.id}`, payload);
        toast.success("Post updated");
      }
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post permanently?")) return;
    try {
      await api.delete(`/admin/blog/${id}`);
      toast.success("Deleted");
      reload();
    } catch {
      toast.error("Delete failed");
    }
  };

  const togglePublish = async (post) => {
    try {
      const newStatus = post.status === "published" ? "draft" : "published";
      await api.patch(`/admin/blog/${post.id}`, { status: newStatus });
      toast.success(newStatus === "published" ? "Published" : "Moved to draft");
      reload();
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <div className="space-y-5" data-testid="blog-view">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#64748B]">{posts.length} post{posts.length !== 1 ? "s" : ""}</p>
        <Button onClick={() => setEditing("new")} className="bg-[#FF008F] hover:bg-[#D60079] text-white" data-testid="new-post">
          <Plus className="w-4 h-4 mr-2" /> New post
        </Button>
      </div>

      <div className="space-y-3">
        {posts.length === 0 && (
          <div className="bg-white rounded-2xl border border-[#E2DDD3] p-10 text-center">
            <p className="font-serif-display text-xl text-[#0F172A]">No posts yet</p>
            <p className="text-sm text-[#64748B] mt-1">Create your first article to start ranking on Google.</p>
          </div>
        )}
        {posts.map((p) => (
          <article key={p.id} className="bg-white rounded-2xl border border-[#E2DDD3] shadow-sm p-5 sm:p-6 flex flex-col sm:flex-row gap-4" data-testid={`post-row-${p.slug}`}>
            {p.cover_image_url && (
              <img src={p.cover_image_url} alt={p.title} className="w-full sm:w-32 h-24 sm:h-24 rounded-lg object-cover shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-start gap-2 flex-wrap">
                <h3 className="font-serif-display text-lg text-[#0F172A]">{p.title}</h3>
                <Badge variant="outline" className={p.status === "published" ? "bg-green-50 text-green-700 border-green-200" : "bg-[#EFEAE0] text-[#64748B] border-[#E2DDD3]"}>
                  {p.status}
                </Badge>
                {p.tags?.[0] && <Badge variant="outline" className="bg-[#FFE5F2] text-[#D60079] border-[#FF008F]/30">{p.tags[0]}</Badge>}
              </div>
              <p className="text-sm text-[#475569] mt-1 line-clamp-2">{p.excerpt}</p>
              <p className="text-xs text-[#94A3B8] mt-2 truncate">/blog/{p.slug}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {p.status === "published" && (
                <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="p-2 text-[#64748B] hover:text-[#FF008F]" title="View live">
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
              <Button variant="outline" size="sm" onClick={() => togglePublish(p)} className="border-[#E2DDD3]" title={p.status === "published" ? "Move to draft" : "Publish"} data-testid={`toggle-${p.slug}`}>
                {p.status === "published" ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setEditing(p)} className="border-[#E2DDD3]" data-testid={`edit-${p.slug}`}>
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => remove(p.id)} className="border-[#E2DDD3] hover:bg-red-50 hover:text-red-600" data-testid={`delete-${p.slug}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </article>
        ))}
      </div>

      {/* Editor sheet */}
      <Sheet open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto bg-[#F7F5F0]" data-testid="blog-editor">
          <SheetHeader>
            <SheetTitle className="font-serif-display text-2xl">{editing === "new" ? "New post" : "Edit post"}</SheetTitle>
            <SheetDescription>Tip: Use Markdown for formatting (## Heading, **bold**, [link](url), - bullets).</SheetDescription>
          </SheetHeader>

          <div className="mt-5 space-y-4">
            <Field label="Title *" >
              <Input value={form.title} onChange={set("title")} className="bg-white border-[#E2DDD3] h-11" data-testid="post-title" />
            </Field>
            <Field label="Slug (URL)">
              <Input value={form.slug} onChange={set("slug")} placeholder="auto-generated if blank" className="bg-white border-[#E2DDD3] h-11" data-testid="post-slug" />
            </Field>
            <Field label="Excerpt (1-2 sentences shown on cards) *">
              <Textarea value={form.excerpt} onChange={set("excerpt")} className="bg-white border-[#E2DDD3] min-h-20" data-testid="post-excerpt" />
            </Field>
            <Field label="Cover image URL">
              <Input value={form.cover_image_url} onChange={set("cover_image_url")} placeholder="https://…" className="bg-white border-[#E2DDD3] h-11" data-testid="post-cover" />
            </Field>
            <Field label="Tags (comma separated)">
              <Input value={form.tags} onChange={set("tags")} placeholder="guide, basics, florida" className="bg-white border-[#E2DDD3] h-11" data-testid="post-tags" />
            </Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="bg-white border-[#E2DDD3]" data-testid="post-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft (not visible)</SelectItem>
                  <SelectItem value="published">Published (live)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Meta description (for SEO — ~150 chars)">
              <Textarea value={form.meta_description} onChange={set("meta_description")} className="bg-white border-[#E2DDD3] min-h-20" data-testid="post-meta" />
            </Field>
            <Field label="Content (Markdown) *">
              <Textarea value={form.content} onChange={set("content")} className="bg-white border-[#E2DDD3] min-h-[420px] font-mono text-sm" placeholder={"## Section\n\nYour content here…"} data-testid="post-content" />
            </Field>
          </div>

          <div className="mt-6 flex items-center justify-between gap-3 sticky bottom-0 bg-[#F7F5F0] pt-4 pb-2 border-t border-[#E2DDD3]">
            <Button variant="outline" onClick={() => setEditing(null)} className="border-[#E2DDD3]">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#FF008F] hover:bg-[#D60079] text-white font-bold" data-testid="save-post">
              {saving ? "Saving…" : (editing === "new" ? "Create post" : "Save changes")}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-[#64748B]">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
